// Turning the backend's raw values into something a person reads.
//
// The portal's endpoints send exact decimals as strings, currency codes beside
// them, and timestamps in ISO 8601 (D6). None of that is presentation, which is
// the point: the decision about how a figure reads belongs to the surface
// showing it, and a payload carrying "$40,004" would have made that decision
// once for every page there will ever be.
//
// So this module is where it is made, and it is made in one place rather than
// in each page, because two pages that format money differently look like two
// products.
//
// Amounts arrive as strings and are parsed with `Number` only at the moment of
// display. That is not a contradiction of the exactness the backend preserved:
// the exact value is what travels and what any future arithmetic would use, and
// the float exists for one call to `Intl.NumberFormat` that is about to round
// to two decimal places anyway.

/** What the locale-aware formatters use where nothing says otherwise. */
const LOCALE: string | undefined = undefined;

/**
 * A money amount with its currency, as the page shows it.
 *
 * Two decimal places, always: a total, a line, a day's cost and an estimate
 * read the same way on every page of this portal (Owner ruling, 2026-10-08).
 * A unit rate is the one exception and has its own function below, because
 * rounding a sub-cent rate to two places would show it as free.
 *
 * @param amount Exact decimal as a string, or null where the backend could not
 *   price the thing at all — which renders as an em dash rather than as zero,
 *   because "we cannot say" and "nothing is owed" are different answers.
 * @param currency ISO 4217 code, or null alongside a null amount.
 */
export function money(amount: string | null, currency: string | null): string {
  if (amount === null || currency === null) {
    return '—';
  }
  return new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(amount));
}

/**
 * A rate per unit, which keeps its cents.
 *
 * A rate of $1.00 and a rate of $1.50 differ by half the bill, so this is the
 * one money figure on these pages that would be wrong to round to whole units.
 */
export function rate(amount: string | null, currency: string | null): string {
  if (amount === null || currency === null) {
    return '—';
  }
  return new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    // Enough places to show a sub-cent rate rather than rounding it to
    // zero, which is how a per-unit price of $0.0025 would otherwise
    // read on the page as free.
    maximumFractionDigits: 6,
  }).format(Number(amount));
}

/**
 * A metered quantity: thousands separated, trailing zeroes dropped.
 *
 * The ledger carries six decimal places because an hour of gas is not a whole
 * number. A month of them usually is, and "29,204" reads better than
 * "29,204.000000" — so the places are shown only where they carry information.
 */
export function quantity(value: string): string {
  const parsed = Number(value);
  return new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: 0,
    maximumFractionDigits: Number.isInteger(parsed) ? 0 : 2,
  }).format(parsed);
}

/** A month, named: "August 2026". */
export function monthName(iso: string | null): string {
  if (!iso) {
    return '—';
  }
  return new Intl.DateTimeFormat(LOCALE, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(iso));
}

/** A month, abbreviated for a table column: "Aug 2026". */
export function monthShort(iso: string | null): string {
  if (!iso) {
    return '—';
  }
  return new Intl.DateTimeFormat(LOCALE, {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(iso));
}

/**
 * The span a billing period covers: "Aug 1 – Aug 31, 2026".
 *
 * Computed from the period's first day rather than sent, because the last day
 * of a month is arithmetic and a payload that carried it would be a payload
 * that can disagree with the calendar.
 */
export function periodSpan(iso: string | null): string {
  if (!iso) {
    return '—';
  }
  const start = new Date(iso);
  // Day zero of the following month is the last day of this one, which
  // is also how February gets its 28th or 29th without a special case.
  const end = new Date(
    Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 0),
  );
  const day = new Intl.DateTimeFormat(LOCALE, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
  return `${day.format(start)} – ${day.format(end)}, ${start.getUTCFullYear()}`;
}

/** The calendar year a timestamp falls in, as a string key. */
export function yearOf(iso: string): string {
  return String(new Date(iso).getUTCFullYear());
}

/** Thresholds for `sinceThen`, largest first. */
const INTERVALS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 60 * 60],
  ['month', 30 * 24 * 60 * 60],
  ['day', 24 * 60 * 60],
  ['hour', 60 * 60],
  ['minute', 60],
];

/**
 * How long ago, in words: "2 hours ago", "yesterday", "just now".
 *
 * The backend sends the instant and nothing else, which is what lets this read
 * correctly in a tab left open for a day — a "2 hours ago" computed on the
 * server would still say that tomorrow.
 */
export function sinceThen(iso: string, now: Date = new Date()): string {
  const seconds = (new Date(iso).getTime() - now.getTime()) / 1000;
  const relative = new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto' });

  for (const [unit, size] of INTERVALS) {
    if (Math.abs(seconds) >= size) {
      return relative.format(Math.round(seconds / size), unit);
    }
  }
  return relative.format(Math.round(seconds), 'second');
}
